import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — W Main Street", address: "2401 W Main Street, Norman, OK 73069", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — W Main Street", address: "1200 W Main Street, Norman, OK 73069", hours: "Call for hours" },
  { name: "CVS Pharmacy — E Lindsey Street", address: "1800 E Lindsey Street, Norman, OK 73071", hours: "Daily 7 AM – 11 PM" },
  { name: "The UPS Store — Norman", address: "120 W Main Street, Norman, OK 73069", hours: "Call for hours" },
  { name: "FedEx Office — Norman", address: "1730 W Lindsey Street, Norman, OK 73069", hours: "Call for hours" },
  { name: "Walgreens — Moore", address: "2200 N Eastern Ave, Moore, OK 73160", hours: "Call for hours" },
  { name: "CVS Pharmacy — Moore", address: "1301 N Eastern Ave, Moore, OK 73160", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Noble", address: "100 E Chestnut, Noble, OK 73068", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Cleveland County Tag Agency — Norman",
    address: "3170 W Robinson Street, Norman, OK 73072",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment strongly recommended. Norman location.",
    featured: true,
  },
  {
    name: "Oklahoma DPS — Norman Driver License Office",
    address: "1760 W Lindsey Street, Norman, OK 73069",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "State driver license and ID services. Call ahead for current wait times.",
    featured: false,
  },
];

export default function NormanHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Norman Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Cleveland County resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Norman</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Norman include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Cleveland County — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Oklahoma driver&apos;s license or REAL ID, these Cleveland County offices handle DMV services.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.ok.gov/dps/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Oklahoma DPS
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times at Cleveland County tag agencies can be long. Check the Oklahoma DPS website for current wait times before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Norman Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Will Rogers World Airport (OKC) is the primary commercial airport for Norman, just 25 miles north. If your trip is coming up fast, make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "OKC",
                    name: "Will Rogers World Airport",
                    distance: "Oklahoma City — about 25 miles north of Norman",
                    address: "7100 Terminal Dr, Oklahoma City, OK 73159",
                    note: "Primary commercial airport for Norman and Cleveland County. Nonstop service to Dallas, Denver, Houston, Chicago, Atlanta, and Las Vegas. Most international travel connects through Dallas or Houston hubs.",
                    flightUrl: "https://www.flyokc.com/",
                    flightLabel: "OKC Airport Website",
                    featured: true,
                  },
                  {
                    code: "TUL",
                    name: "Tulsa International Airport",
                    distance: "Tulsa — about 120 miles northeast of Norman",
                    address: "7777 E Apache Street, Tulsa, OK 74115",
                    note: "Tulsa option for Norman travelers seeking additional nonstop routes. Also home to Fast Passport Center service coverage.",
                    flightUrl: "https://www.tulsaairports.com/",
                    flightLabel: "TUL Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}